import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { setNavigator } from "shared/utils";
import { AuthPage } from "pages/AuthPage";
import { Error404Page } from "pages/Error404Page";
import { debounce, EventEmitter } from "@ismael1361/utils";
import { useAuthState } from "shared/hooks";
import { UserServices } from "shared/services";
import { IAccountPermissionLevel } from "shared/model";
import { WithoutPermissionPage } from "pages/WithoutPermissionPage";
import { MainLayout } from "layouts";

interface RouteOptions {
	requiresAuth?: boolean;
	minimumPermission?: IAccountPermissionLevel;
}

type RouterElement = (() => React.ReactNode) | React.FC;

const routesList: { path: string; Element: RouterElement; options?: RouteOptions }[] = [];

const emitter = new EventEmitter<{
	routesChanged: [routes: typeof routesList];
}>();

emitter.prepared = true;

const emitRoutesChanged = debounce(() => {
	emitter.emit("routesChanged", routesList);
}, 200);

export const addRoute = (path: string, Element: RouterElement, options: RouteOptions = {}) => {
	routesList.push({ path, Element, options });
	emitRoutesChanged();
};

const NavigationSetup = () => {
	const navigate = useNavigate();

	useEffect(() => {
		setNavigator(navigate);
	}, [navigate]);

	return null;
};

const ToLogin: React.FC<{
	children?: React.ReactNode;
}> = ({ children }) => {
	const [show, setShow] = React.useState(false);
	const navigate = useNavigate();
	const userStatus = useAuthState();

	useEffect(() => {
		const time = setTimeout(() => {
			if (UserServices.currentToken || UserServices.currentUser || userStatus === "signed") {
				setShow(true);
				return;
			}
			navigate("/auth/login", { replace: true });
		}, 500);

		return () => clearTimeout(time);
	}, [navigate, userStatus]);

	return <>{userStatus === "signed" || show ? children : null}</>;
};

const AppRoutes = () => {
	const [prepared, setPrepared] = React.useState(false);
	const [routes, setRoutes] = React.useState(routesList);
	const userStatus = useAuthState();

	useEffect(() => {
		let time: NodeJS.Timeout;

		setRoutes(() => [...routesList]);

		const prepare = () => {
			clearTimeout(time);

			time = setTimeout(() => {
				setPrepared(true);
			}, 500);
		};

		const event = emitter.on("routesChanged", (routes) => {
			setRoutes(() => [...routes]);
			prepare();
		});

		prepare();

		return () => {
			event.stop();
			clearTimeout(time);
		};
	}, [userStatus]);

	return (
		<BrowserRouter>
			<NavigationSetup />
			<Routes>
				{!prepared ? (
					<Route
						path="*"
						element={<></>}
					/>
				) : (
					<>
						{userStatus !== "loading" &&
							routes.map(({ path, Element, options = {} }, index) => {
								const { requiresAuth = false, minimumPermission = 0 } = options;

								if (UserServices.currentUser?.containsPermissionBy(minimumPermission) === false) {
									return (
										<Route
											key={index}
											path={path}
											element={<WithoutPermissionPage />}
										/>
									);
								}

								return (
									<Route
										key={index}
										path={path}
										element={
											requiresAuth && userStatus !== "signed" ? (
												<ToLogin>
													<Element />
												</ToLogin>
											) : (
												<Element />
											)
										}
									/>
								);
							})}
						<Route
							path="/auth/*"
							element={
								userStatus === "unsigned" ? (
									<AuthPage />
								) : (
									<Navigate
										to="/"
										replace
									/>
								)
							}
						/>
						<Route
							path="*"
							element={
								userStatus === "signed" ? (
									<MainLayout>
										<Error404Page />
									</MainLayout>
								) : userStatus === "unsigned" ? (
									<Navigate
										to="/auth/login"
										replace
									/>
								) : null
							}
						/>
					</>
				)}
			</Routes>
		</BrowserRouter>
	);
};

export default AppRoutes;
